import * as Sentry from '@sentry/browser'
import { Socket } from 'phoenix'
import { LiveSocket } from 'phoenix_live_view'
import { isFunction, isObjectType } from 'remeda'

import 'phoenix_html'

import '../facade/js'
import {
  createFacadeDomCallbacks,
  FacadeHooks,
  facadeUploaders,
  setGridRenderReporter,
  setupFacadeLiveView,
  setupNavigationTiming
} from '../facade/js/liveview'
import { initBrowserSupportCheck } from './browser-support'
import { sentryGridRenderReporter } from './grid-render-reporting'
import { reportNavigation } from './navigation-reporting'

// Private types ---------------------------------------------------------------

interface SentryWindow extends Window {
  readonly SENTRY?: {
    readonly id?: string
    readonly locale?: string
    readonly company?: string
    readonly company_id?: string
    readonly account_id?: string
  }
}

type LiveReloader = {
  readonly enableServerLogs: () => void
  readonly enableEditorShortcuts: (keys: {
    readonly caller: string
    readonly definition: string
  }) => void
}

// Sentry type
declare let window: SentryWindow

// Private functions -----------------------------------------------------------

// Checks the live-reload client's API across its iframe boundary.
const isLiveReloader = (value: unknown): value is LiveReloader =>
  isObjectType(value) &&
  'enableServerLogs' in value &&
  isFunction(value.enableServerLogs) &&
  'enableEditorShortcuts' in value &&
  isFunction(value.enableEditorShortcuts)

// Configures production error reporting and grid performance telemetry.
const configureSentry = (): void => {
  Sentry.init({
    dsn: process.env.SENTRY_DSN_FRONTEND,
    environment: 'prod',
    release: process.env.NF_GIT_SHA,
    integrations: [
      Sentry.browserTracingIntegration({
        webVitals: { softNavigations: false, bfcacheNavigations: false },
        // Phoenix LiveView's longpoll fallback issues long-lived HTTP poll
        // requests to /live/longpoll. Tracing them produces multi-second
        // http.client spans that are long by design, not real slowness, so we
        // skip span creation for them rather than disconnecting live clients.
        shouldCreateSpanForRequest: (url) => !url.includes('/live/longpoll')
      }),
      Sentry.browserProfilingIntegration(),
      Sentry.browserSessionIntegration({ lifecycle: 'route' }),
      Sentry.userTimingIntegration()
    ],
    // Keep v10 transaction reporting and collection limits after the SDK upgrade.
    traceLifecycle: 'static',
    attachStacktrace: false,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] }
      },
      httpBodies: [],
      urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false }
    },
    tracesSampleRate: 0.1,
    // v11 samples profiles once per page session instead of per transaction.
    profileSessionSampleRate: 0.01,
    profileLifecycle: 'trace',

    // Filter and process errors before sending to Sentry
    beforeSend: (event, hint) => {
      const error = hint.originalException

      // Filter out ComboboxItemsConflictError - this is a known configuration warning
      // that doesn't impact functionality and was creating 40k+ events per week
      if (
        error &&
        typeof error === 'object' &&
        ('name' in error || 'message' in error)
      ) {
        const errorName = 'name' in error ? String(error.name) : ''
        const errorMessage = 'message' in error ? String(error.message) : ''

        // Drop ComboboxItemsConflictError entirely
        if (
          errorName === 'ComboboxItemsConflictError' ||
          errorMessage.includes('Both items and dataProvider are configured')
        ) {
          return null
        }

        // Drop FormTriggerError - these are typically non-critical timing issues
        if (errorName === 'FormTriggerError') {
          return null
        }
      }

      // Improve fingerprinting for better error grouping
      if (event.exception?.values?.[0]) {
        const exception = event.exception.values[0]
        const errorType = exception.type || 'UnknownError'
        const component = String(event.tags?.component || 'unknown')
        const operation = String(event.tags?.operation || 'unknown')
        const eventName = String(event.tags?.event || 'unknown')

        // Create more specific fingerprints for better deduplication
        if (errorType === 'GridDataOperationError') {
          event.fingerprint = ['grid-operation', component, operation]
        } else if (errorType === 'LiveViewPushError') {
          event.fingerprint = ['liveview-push', component, eventName]
        } else {
          // Default: group by error type and component
          event.fingerprint = [errorType, component]
        }
      }

      return event
    },

    // Ignore common non-actionable errors
    ignoreErrors: [
      // Browser extension errors
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      // Network errors that we can't control
      'NetworkError',
      'Failed to fetch',
      'Network request failed',
      'Load failed',
      'AbortError',
      'cancelled',
      // ResizeObserver errors (benign browser quirk)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Non-actionable errors from rendered Mustache print templates (KERUJ-FRONTEND-K6)
      'Error invoking post: Method not found',
      // LiveView internal race condition during popstate navigation (KERUJ-FRONTEND-K7)
      // https://github.com/phoenixframework/phoenix_live_view/issues/737
      /Cannot read properties of null \(reading 'isConnected'\)/
    ]
  })

  Sentry.setUser({
    id: window.SENTRY?.id
  })

  Sentry.setContext('character', {
    locale: window.SENTRY?.locale,
    company: window.SENTRY?.company,
    company_id: window.SENTRY?.company_id,
    account_id: window.SENTRY?.account_id
  })

  setGridRenderReporter(sentryGridRenderReporter)

  // Reports otherwise silent global promise rejections.
  const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    if (!event.reason) {
      return
    }
    Sentry.captureException(event.reason, {
      level: 'error',
      tags: { source: 'unhandled-promise-rejection' }
    })
  }

  window.addEventListener('unhandledrejection', handleUnhandledRejection)
}

// Reads the CSRF token supplied by the Phoenix layout.
const csrfToken = (): string | null | undefined =>
  document?.querySelector("meta[name='csrf-token']")?.getAttribute('content')

// Sends held modifiers with keydown events, so bindings can tell Cmd+F8 from F8.
const keyModifiers = (
  event: KeyboardEvent
): { readonly ctrlKey: boolean; readonly metaKey: boolean } => ({
  ctrlKey: event.ctrlKey,
  metaKey: event.metaKey
})

// Connects the application's transport with Facade's UI integration.
const createLiveSocket = () =>
  new LiveSocket('/live', Socket, {
    timeout: 45000,
    longPollFallbackMs: 6000,
    uploaders: facadeUploaders,
    params: { _csrf_token: csrfToken() },
    metadata: { keydown: keyModifiers },
    hooks: {
      ...FacadeHooks
    },
    dom: createFacadeDomCallbacks((error) => {
      Sentry.captureException(error, { tags: { component: 'WebAwesome' } })
    })
  })

// Enables development diagnostics through Phoenix's live-reload client.
const handleLiveReloadAttached = (event: Event): void => {
  if ('detail' in event && isLiveReloader(event.detail)) {
    const reloader = event.detail

    reloader.enableServerLogs()
    reloader.enableEditorShortcuts({ caller: 'c', definition: 'd' })
    Object.assign(window, { liveReloader: reloader })
  }
}

// Module setup ----------------------------------------------------------------

if (process.env.NODE_ENV === 'production') {
  configureSentry()
}

setupFacadeLiveView()
setupNavigationTiming(reportNavigation)

const liveSocket = createLiveSocket()

// Keep diagnostics opt-in before LiveView applies its localhost debug default.
if (!liveSocket.isDebugEnabled()) {
  liveSocket.disableDebug()
}

if (process.env.NODE_ENV === 'development') {
  Object.assign(window, { liveSocket })
  window.addEventListener('phx:live_reload:attached', handleLiveReloadAttached)
}

initBrowserSupportCheck()

// connect if there are any LiveViews on the page
liveSocket.connect()
